const DEFAULT_CURRENCY_LOCALE = 'de-CH';
const DEFAULT_NUMBER_LOCALE = 'de-CH';
const SURFACE_DECIMALS = 0;
const DETAIL_DECIMALS = 2;

const toThinSpace = (value: string) => value.replace(/\u00A0/g, '\u202F');

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const parseNumeric = (value: unknown): number | null => {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

type SignDisplay = 'auto' | 'always' | 'never';

interface CurrencyFormatOptions {
  locale?: string;
  decimals?: number;
  sign?: SignDisplay;
  currency?: string;
}

export const formatCurrency = (
  rawValue: unknown,
  { locale = DEFAULT_CURRENCY_LOCALE, decimals = SURFACE_DECIMALS, sign = 'auto', currency = 'CHF' }: CurrencyFormatOptions = {},
): string => {
  const value = parseNumeric(rawValue);
  if (value === null) return '—';

  const absValue = Math.abs(value);
  const formatter = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formatted = toThinSpace(formatter.format(absValue));

  const resolvedSign =
    sign === 'never'
      ? ''
      : value < 0
        ? '-'
        : sign === 'always'
          ? '+'
          : '';

  return `${resolvedSign}${currency} ${formatted}`;
};

interface NumberFormatOptions {
  locale?: string;
  decimals?: number;
  sign?: SignDisplay;
}

export const formatNumber = (
  rawValue: unknown,
  { locale = DEFAULT_NUMBER_LOCALE, decimals = SURFACE_DECIMALS, sign = 'auto' }: NumberFormatOptions = {},
): string => {
  const value = parseNumeric(rawValue);
  if (value === null) return '—';

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: sign,
  });

  return toThinSpace(formatter.format(Number(value)));
};

interface PercentFormatOptions {
  locale?: string;
  decimals?: number;
  sign?: SignDisplay;
}

export const formatPercent = (
  rawValue: unknown,
  { locale = DEFAULT_NUMBER_LOCALE, decimals = 1, sign = 'auto' }: PercentFormatOptions = {},
): string => {
  const value = parseNumeric(rawValue);
  if (value === null) return '—';

  const formatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: sign,
  });

  return formatter.format(Number(value) / 100);
};

type CurrencySurfaceOptions = Omit<CurrencyFormatOptions, 'decimals'>;
type CurrencyTooltipOptions = CurrencySurfaceOptions & { decimals?: number };
type NumberSurfaceOptions = Omit<NumberFormatOptions, 'decimals'>;
type NumberTooltipOptions = NumberSurfaceOptions & { decimals?: number };

export const formatCurrencySurface = (
  rawValue: unknown,
  options?: CurrencySurfaceOptions,
): string => formatCurrency(rawValue, { ...options, decimals: SURFACE_DECIMALS });

export const formatCurrencyTooltip = (
  rawValue: unknown,
  options?: CurrencyTooltipOptions,
): string => {
  const { decimals = DETAIL_DECIMALS, ...rest } = options ?? {};
  return formatCurrency(rawValue, { ...rest, decimals });
};

export const formatNumberSurface = (
  rawValue: unknown,
  options?: NumberSurfaceOptions,
): string => formatNumber(rawValue, { ...options, decimals: SURFACE_DECIMALS });

export const formatNumberTooltip = (
  rawValue: unknown,
  options?: NumberTooltipOptions,
): string => {
  const { decimals = DETAIL_DECIMALS, ...rest } = options ?? {};
  return formatNumber(rawValue, { ...rest, decimals });
};

export const formatCurrencyForExport = (
  rawValue: unknown,
  locale?: string,
): string => formatCurrencyTooltip(rawValue, { locale, sign: 'auto' });

export const formatRatioForExport = (
  rawValue: unknown,
  locale?: string,
): string => formatNumberTooltip(rawValue, { locale, sign: 'auto' });

export const formatNumberForExport = (
  rawValue: unknown,
  locale?: string,
): string => formatNumberTooltip(rawValue, { locale, sign: 'auto' });

export const formatChfSurface = (
  rawValue: unknown,
  options: CurrencySurfaceOptions = {},
): string => formatCurrencySurface(rawValue, options);

export const formatChfDetailed = (
  rawValue: unknown,
  options: CurrencyTooltipOptions = {},
): string => formatCurrencyTooltip(rawValue, options);

export const formatSwissNumberSurface = (
  rawValue: unknown,
  options: NumberSurfaceOptions = {},
): string => formatNumberSurface(rawValue, options);

export const formatSwissNumberDetailed = (
  rawValue: unknown,
  options: NumberTooltipOptions = {},
): string => formatNumberTooltip(rawValue, options);

export const ensureLocale = (tenantLocale?: string | null): string =>
  tenantLocale && typeof tenantLocale === 'string'
    ? tenantLocale
    : DEFAULT_CURRENCY_LOCALE;

export const thinSpace = toThinSpace;
export { parseNumeric };
